import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import PlaceholderSvg from 'inline-icons/placeholder.svg';
import { roundEdgeDevices } from '../../constants';
import { imageBorderRadius } from '../../themes';

const PlaceholderImageContainer = styled.div`
  position: relative;
  ${({ containerStyles }) => containerStyles}
`;

const Image = styled.img`
  opacity: 0;
  transition: opacity 300ms;
  max-width: 100%;
  height: auto;

  ${({ styles }) => styles}

  // Using position absolute to keep the image from displacing
  // any other DOM elements when loading in
  ${({ loaded }) => (loaded ? 'opacity: 1;' : 'position: absolute;')}
  ${({ isSquircle }) => {
    if (isSquircle) {
      return `mask: url(/device-images/squircle.png) 50% 0% / 100% no-repeat; -webkit-mask: url(/device-images/squircle.png) 50% 0% / 100% no-repeat;`;
    } else {
      return `border-radius: ${imageBorderRadius};`;
    }
  }}
`;

// Using an svg to placehold for the image while loading
const Svg = styled.svg`
  max-width: 100%;
  height: auto;
  fill: #e2e2e2;

  ${({ hide }) => (hide ? 'display: none;' : '')}
`;

const Placeholder = styled.div.attrs({
  dangerouslySetInnerHTML: {
    __html: PlaceholderSvg
  }
})`
  ${({ hide }) => (hide ? 'display: none;' : '')}
`;

const SvgOrIconPlaceholder = ({
  showIconPlaceholder,
  hide,
  width,
  height,
  ...props
}) => {
  const numericWidth = typeof width === 'number' ? width : parseInt(width, 10);
  const numericHeight =
    typeof height === 'number' ? height : parseInt(height, 10);
  const viewBox =
    numericWidth && numericHeight
      ? `0 0 ${numericWidth} ${numericHeight}`
      : undefined;

  return showIconPlaceholder ? (
    <Placeholder hide={hide} />
  ) : (
    <Svg
      hide={hide}
      width={width}
      height={height}
      viewBox={viewBox}
      {...props}
    />
  );
};

class ImageContainer extends Component {
  static propTypes = {
    containerAttr: PropTypes.shape({
      iconPlaceholder: PropTypes.bool,
      styles: PropTypes.oneOfType([PropTypes.shape(), PropTypes.string])
    }).isRequired,
    imageAttr: PropTypes.shape({
      alt: PropTypes.string.isRequired,
      src: PropTypes.string.isRequired,
      styles: PropTypes.oneOfType([PropTypes.shape(), PropTypes.string])
    }).isRequired,
    isSquircle: PropTypes.bool
  };

  state = {
    loaded: false
  };

  handleImageLoaded = () => {
    this.setState({
      loaded: true
    });
  };

  get device() {
    const { currentUser: { device = {} } = {} } = this.props;

    return device;
  }

  get deviceName() {
    return (this.device.productName || '').toLowerCase();
  }

  get isSquircle() {
    return roundEdgeDevices.includes(this.deviceName);
  }

  render() {
    const {
      children,
      className,
      containerAttr,
      imageAttr,
      ...props
    } = this.props;
    const {
      width,
      height,
      styles: containerStyles,
      iconPlaceholder,
      ...containerProps
    } = containerAttr;
    const { loaded } = this.state;

    return (
      <PlaceholderImageContainer
        containerStyles={containerStyles}
        className={className}
        imageLoaded={loaded}
        {...containerProps}
        {...props}
      >
        <SvgOrIconPlaceholder
          showIconPlaceholder={iconPlaceholder}
          width={width}
          height={height}
          hide={loaded}
        >
          <rect width="100%" height="100%" />
        </SvgOrIconPlaceholder>
        <Image
          onLoad={this.handleImageLoaded}
          loaded={loaded}
          {...imageAttr}
          isSquircle={this.isSquircle}
        />
        {children}
      </PlaceholderImageContainer>
    );
  }
}

const mapStateToProps = (state) => ({
  currentUser: state.registry.currentUser
});

export default connect(mapStateToProps)(ImageContainer);
